import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Siren, ClipboardList } from "lucide-react";
import { CLAIM_CATEGORIES, CLAIM_CHECKLISTS } from "@/data/claimsData";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-600/20";

export default function ClaimIntakeForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    policy_category: "", date_of_loss: "", loss_location: "", description: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const checklist = form.policy_category ? CLAIM_CHECKLISTS[form.policy_category] || [] : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.policy_category) {
      toast.error(t("form.selectType"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/claims`, form);
      toast.success(t("cf.success"));
      setForm({ name: "", company: "", phone: "", email: "", policy_category: "", date_of_loss: "", loss_location: "", description: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("cf.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
      <form data-testid="claim-intake-form" onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("form.name")}</label>
          <input id="cf-name" data-testid="claim-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t("form.namePh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-company" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("form.company")}</label>
          <input id="cf-company" data-testid="claim-company-input" value={form.company} onChange={set("company")} placeholder={t("form.companyPh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("form.phone")}</label>
          <input id="cf-phone" data-testid="claim-phone-input" required minLength={8} value={form.phone} onChange={set("phone")} placeholder={t("form.phonePh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("form.email")}</label>
          <input id="cf-email" data-testid="claim-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t("form.emailPh")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-category" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("cf.category")}</label>
          <select id="cf-category" data-testid="claim-category-select" required value={form.policy_category} onChange={set("policy_category")} className={inputCls}>
            <option value="" disabled>{t("cf.categoryPh")}</option>
            {CLAIM_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cf-date" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("cf.date")}</label>
          <input id="cf-date" data-testid="claim-date-input" type="date" value={form.date_of_loss} onChange={set("date_of_loss")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cf-location" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("cf.location")}</label>
          <input id="cf-location" data-testid="claim-location-input" value={form.loss_location} onChange={set("loss_location")} placeholder={t("cf.locationPh")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cf-desc" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("cf.desc")}</label>
          <textarea id="cf-desc" data-testid="claim-description-input" required minLength={10} rows={4} value={form.description} onChange={set("description")} placeholder={t("cf.descPh")} className={`${inputCls} resize-none`} />
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit"
            data-testid="claim-submit-button"
            disabled={loading}
            className="flex items-center gap-2 bg-red-700 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-800 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Siren className="h-4 w-4" />}
            {loading ? t("cf.submitting") : t("cf.submit")}
          </button>
        </div>
      </form>

      <div className="lg:col-span-5">
        <div data-testid="claim-checklist-panel" className="border border-amber-500/40 bg-amber-50/60 p-6 sm:p-8 lg:sticky lg:top-32">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-navy">
            <ClipboardList className="h-5 w-5 text-amber-700" />
            {t("cf.checkTitle")}
          </h3>
          {checklist.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">{t("cf.categoryPh")}…</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {checklist.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border border-amber-600/60" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
